import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import Icon, { type IconName } from '../components/Icon';
import { ROLE_LABEL, initialsOf } from '../features/team/roleMeta';
import type { CompanySettings } from '../types';

interface NavEntry {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}

interface NavSection {
  label: string;
  icon: IconName;
  items: NavEntry[];
}

/**
 * The nav is built from the business model, not fixed.
 *
 * A service company has no journey plans — there is no fixed list of
 * shops to plan against, which is the definition of the mode — so
 * showing the link and then explaining it does not apply is clutter
 * where a missing item is simply correct.
 */
function sectionsFor(settings: CompanySettings | null): NavSection[] {
  const isService = settings?.businessModel === 'service';
  const customersOn = settings?.customersEnabled !== false;
  const leadsOn = settings?.leadsEnabled === true;
  return [
  ...(customersOn ? [{
    label: 'Customers',
    icon: 'users' as IconName,
    items: [
      { to: '/customers', label: 'All customers', icon: 'users' as IconName, end: true },
      { to: '/customers/areas', label: 'Areas / Routes', icon: 'map' as IconName },
      { to: '/customers/form', label: 'Customer form', icon: 'sliders' as IconName },
    ],
  }] : []),
  ...(leadsOn ? [{
    label: 'Leads',
    icon: 'users' as IconName,
    items: [
      { to: '/leads', label: 'All leads', icon: 'users' as IconName, end: true },
      { to: '/leads/dashboard', label: 'Dashboard', icon: 'home' as IconName },
      { to: '/leads/pipeline', label: 'Pipeline & statuses', icon: 'sliders' as IconName },
      { to: '/leads/form', label: 'Lead form', icon: 'sliders' as IconName },
    ],
  }] : []),
  {
    label: 'Visits',
    icon: 'map',
    items: [
      { to: '/visits', label: isService ? 'Visits & map' : 'Visits & coverage', icon: 'map', end: true },
      ...(isService ? [] : [
        { to: '/visits/plans', label: 'Route schedule', icon: 'checklist' as IconName },
      { to: '/visits/schedule-report', label: 'Schedule report', icon: 'checklist' as IconName },
      ]),
      { to: '/visits/form', label: 'Visit form', icon: 'sliders' },
    ],
  },
  ...(settings?.ordersEnabled ? [{
    label: 'Orders',
    icon: 'list' as IconName,
    items: [
      { to: '/orders', label: 'Orders', icon: 'list' as IconName, end: true },
      { to: '/orders/products', label: 'Products', icon: 'list' as IconName },
      { to: '/orders/masters', label: 'Product lists', icon: 'sliders' as IconName },
      { to: '/orders/price-lists', label: 'Price lists', icon: 'sliders' as IconName },
    ],
  }] : []),
  ...(settings?.trackingEnabled ? [{
    label: 'Tracking',
    icon: 'pin' as IconName,
    items: [
      { to: '/tracking', label: 'Team map', icon: 'map' as IconName, end: true },
      { to: '/tracking/replay', label: 'Route replay', icon: 'history' as IconName },
    ],
  }] : []),
  {
    label: 'Reports',
    icon: 'checklist',
    items: [
      { to: '/reports/dashboard', label: 'Visit dashboard', icon: 'home' as IconName },
      { to: '/reports/analytics', label: 'Visit analytics', icon: 'checklist' as IconName },
      // Ask AI only exists for a company once the platform has turned it on.
      ...(settings?.assistantEnabled ? [{ to: '/reports/ask', label: 'Ask AI', icon: 'search' as IconName }] : []),
      { to: '/reports/visits', label: 'Visit report', icon: 'checklist' as IconName },
      ...(settings?.ordersEnabled
        ? [
          { to: '/reports/orders', label: 'Order report', icon: 'list' as IconName },
          { to: '/reports/order-lines', label: 'Order line report', icon: 'list' as IconName },
        ]
        : []),
    ],
  },
  {
    label: 'Leave & calendar',
    icon: 'date',
    items: [
      { to: '/leave/requests', label: 'Leave requests', icon: 'checklist' as IconName },
      { to: '/leave/types', label: 'Leave types', icon: 'sliders' as IconName },
      { to: '/leave/calendar', label: 'Company calendar', icon: 'date' as IconName },
    ],
  },
  {
    label: 'Expenses',
    icon: 'download',
    items: [
      { to: '/expenses', label: 'Dashboard', icon: 'home' as IconName, end: true },
      { to: '/expenses/claims', label: 'Claims & approvals', icon: 'checklist' as IconName },
      { to: '/expenses/categories', label: 'Categories & rates', icon: 'sliders' as IconName },
    ],
  },
  // Dispatch only exists when the platform has turned it on for this company.
  ...(settings?.dispatchEnabled ? [{
    label: 'Dispatch',
    icon: 'download' as IconName,
    items: [
      { to: '/dispatch', label: 'Dispatch sheets', icon: 'list' as IconName, end: true },
      { to: '/dispatch/reasons', label: 'Non-delivery reasons', icon: 'sliders' as IconName },
    ],
  }] : []),
  // Distributors: off by default, turned on per company by the platform.
  ...(settings?.distributorEnabled ? [{
    label: 'Distributors',
    icon: 'users' as IconName,
    items: [
      { to: '/distributors', label: 'All distributors', icon: 'users' as IconName, end: true },
      { to: '/distributors/areas', label: 'Area assignments', icon: 'map' as IconName },
    ],
  }] : []),
  {
    label: 'Company',
    icon: 'id',
    items: [
      { to: '/team', label: 'Team', icon: 'id' },
      { to: '/settings', label: 'Settings', icon: 'sliders' },
    ],
  },
  ];
}

/** Which section owns the current path (for auto-expand + highlight). */
function activeSectionLabel(sections: NavSection[], pathname: string): string | undefined {
  return sections.find((s) => s.items.some(
    (it) => pathname === it.to || pathname.startsWith(`${it.to}/`),
  ))?.label;
}

export default function AppShell() {
  const { user, company, signOut, impersonating, stopImpersonating } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  // One request for the whole session. A failure here only means the nav
  // shows its default shape, so it is deliberately not surfaced.
  useEffect(() => {
    let cancelled = false;
    api.get<CompanySettings>('/settings')
      .then((data) => { if (!cancelled) setSettings(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const sections = useMemo(() => sectionsFor(settings), [settings]);
  const activeLabel = activeSectionLabel(sections, location.pathname);

  // The group that owns the current page is always expanded, so navigating
  // never leaves the current page's group collapsed.
  useEffect(() => {
    if (activeLabel) setOpen((o) => (o[activeLabel] ? o : { ...o, [activeLabel]: true }));
  }, [activeLabel]);

  const toggle = (label: string) => setOpen((o) => ({ ...o, [label]: !o[label] }));

  const backToPlatform = async () => {
    await stopImpersonating();
    navigate('/platform');
  };

  return (
    <>
      {impersonating ? (
        <div className="imp-banner">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon name="eye" size={14} />
            Viewing as <strong>{company?.name}</strong>
          </span>
          <button type="button" className="btn btn-sm" onClick={backToPlatform}
            style={{ background: 'rgba(255,255,255,.2)', color: '#fff', border: 'none' }}>
            <Icon name="chevron-left" size={14} /> Back to platform
          </button>
        </div>
      ) : null}

      <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          {company?.logoUrl
            ? <img className="brand-logo" src={company.logoUrl} alt={company.name} />
            : <div className="brand-mark">{company?.name?.[0]?.toUpperCase() ?? 'S'}</div>}
          <div style={{ minWidth: 0 }}>
            <div className="brand-name">{company?.name ?? 'Salestrendz'}</div>
          </div>
        </div>

        <nav className="nav">
          {sections.map((section) => {
            const isOpen = !!open[section.label];
            const isCurrent = section.label === activeLabel;
            return (
              <div className="nav-group" key={section.label}>
                <button
                  type="button"
                  className={`nav-group-head${isCurrent ? ' current' : ''}`}
                  aria-expanded={isOpen}
                  onClick={() => toggle(section.label)}
                >
                  <Icon name={section.icon} size={18} />
                  <span className="grow">{section.label}</span>
                  <Icon name="chevron-down" size={15}
                    className={`nav-caret${isOpen ? ' open' : ''}`} />
                </button>
                {isOpen ? (
                  <div className="nav-sub">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) => `nav-subitem${isActive ? ' active' : ''}`}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-foot">
          <div className="who">
            <div className="avatar">{initialsOf(user?.fullName ?? '?')}</div>
            <div className="who-text grow">
              <div className="who-name">{user?.fullName}</div>
              <div className="who-role">{user ? ROLE_LABEL[user.role] : ''}</div>
            </div>
            <button
              type="button"
              className="icon-btn"
              title="Sign out"
              aria-label="Sign out"
              onClick={() => void signOut()}
            >
              <Icon name="logout" size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        <Outlet />
      </main>
    </div>
    </>
  );
}

/** Every page renders its own header through this so they stay identical. */
export function PageHeader({
  title, subtitle, actions,
}: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <header className="topbar">
      <div style={{ minWidth: 0 }}>
        <h1>{title}</h1>
        {subtitle ? <p className="topbar-sub">{subtitle}</p> : null}
      </div>
      {actions ? <div className="row gap-8">{actions}</div> : null}
    </header>
  );
}
